import React, { useEffect, useState, useCallback } from "react";
import { Modal, Spinner, Alert } from "react-bootstrap";
import {
  FileText,
  Calendar,
  CheckCircle2,
  Search,
  Tag,
  File,
  FileSpreadsheet,
  FileImage,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { AppButton } from "../ui";

const FILE_ICONS = {
  PDF: { icon: FileText, color: "#ef4444" },
  PPT: { icon: FileText, color: "#f97316" },
  PPTX: { icon: FileText, color: "#f97316" },
  DOC: { icon: FileText, color: "#3b82f6" },
  DOCX: { icon: FileText, color: "#3b82f6" },
  XLS: { icon: FileSpreadsheet, color: "#22c55e" },
  XLSX: { icon: FileSpreadsheet, color: "#22c55e" },
  JPG: { icon: FileImage, color: "#8b5cf6" },
  JPEG: { icon: FileImage, color: "#8b5cf6" },
  PNG: { icon: FileImage, color: "#8b5cf6" },
  TXT: { icon: FileText, color: "#6b7280" },
  ZIP: { icon: File, color: "#eab308" },
  RAR: { icon: File, color: "#eab308" },
};

const CATEGORY_LABELS = { ppt: "PPT", script: "Script", notes: "Notes", other: "Other" };

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata",
  });
};

/**
 * MapMaterialModal
 *
 * Modal to select a batch material and link it to a video or syllabus topic.
 *
 * Props:
 *   - show        : Boolean
 *   - batchId     : String
 *   - targetLabel : String (display: e.g. topic name or video title)
 *   - alreadyLinkedIds : String[] — material IDs already linked (shown as "linked")
 *   - onLink      : Function(materialId) — async, may throw
 *   - onClose     : Function
 */
const MapMaterialModal = ({ show, batchId, targetLabel, alreadyLinkedIds = [], onLink, onClose }) => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const fetchMaterials = useCallback(async () => {
    if (!batchId) return;
    setLoading(true);
    setError("");
    setSelected(null);
    setSearch("");
    try {
      const { data } = await apiClient.get(`/materials/batch/${batchId}`, { params: { limit: 200 } });
      // Combine batch + course materials
      const all = [...(data.data?.batchMaterials || []), ...(data.data?.courseMaterials || [])];
      // Only show ready materials
      setMaterials(all.filter((m) => m.status === "ready"));
    } catch {
      setError("Failed to load materials.");
      setMaterials([]);
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    if (show) fetchMaterials();
  }, [show, fetchMaterials]);

  const handleLink = async () => {
    if (!selected) return;
    setLinking(true);
    setError("");
    try {
      await onLink(selected);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to link material.");
    } finally {
      setLinking(false);
    }
  };

  const linkedSet = new Set(alreadyLinkedIds.map(String));

  const filtered = materials.filter((m) => {
    const q = search.toLowerCase();
    return (
      (m.title || "").toLowerCase().includes(q) ||
      (m.topic || "").toLowerCase().includes(q) ||
      (m.category || "").toLowerCase().includes(q)
    );
  });

  return (
    <Modal show={show} onHide={onClose} centered size="lg" className="map-recording-modal">
      <Modal.Header closeButton className="mrm-header">
        <Modal.Title className="mrm-title">
          <FileText size={20} className="me-2" />
          Link Material
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="mrm-body">
        {targetLabel && (
          <div className="mrm-topic-info">
            Linking material to: <strong>{targetLabel}</strong>
          </div>
        )}

        <div className="mrm-search">
          <Search size={16} className="mrm-search-icon" />
          <input
            type="text"
            className="mrm-search-input"
            placeholder="Search materials by title, topic, or category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {error && (
          <Alert variant="danger" className="mb-3">{error}</Alert>
        )}

        {loading ? (
          <div className="mrm-loading">
            <Spinner animation="border" size="sm" />
            <span>Loading materials…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="mrm-empty">
            <FileText size={32} />
            <p>
              {materials.length === 0
                ? "No materials available for this batch."
                : "No materials match your search."}
            </p>
          </div>
        ) : (
          <div className="mrm-list">
            {filtered.map((mat) => {
              const isLinked = linkedSet.has(String(mat._id));
              const fileInfo = FILE_ICONS[mat.fileType] || { icon: File, color: "#6b7280" };
              const IconComp = fileInfo.icon;

              return (
                <div
                  key={mat._id}
                  className={`mrm-item ${selected === mat._id ? "mrm-item-selected" : ""} ${isLinked ? "mrm-item-linked" : ""}`}
                  onClick={() => !isLinked && setSelected(mat._id)}
                  role="button"
                  tabIndex={0}
                  style={isLinked ? { opacity: 0.55, cursor: "default" } : undefined}
                >
                  {/* File icon */}
                  <div className="mrm-thumb" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <IconComp size={24} style={{ color: fileInfo.color }} />
                  </div>

                  {/* Info */}
                  <div className="mrm-item-info">
                    <span className="mrm-item-topic">{mat.title}</span>
                    <span className="mrm-item-date">
                      <Calendar size={12} />
                      {formatDate(mat.sessionDate || mat.createdAt)}
                      {mat.category && (
                        <span className="mrm-file-type">{CATEGORY_LABELS[mat.category] || mat.category}</span>
                      )}
                      {mat.fileType && (
                        <span className="mrm-file-type">{mat.fileType}</span>
                      )}
                    </span>
                    {mat.topic && (
                      <span className="mrm-item-date">
                        <Tag size={11} />
                        {mat.topic}
                      </span>
                    )}
                  </div>

                  {/* Linked / selected indicator */}
                  {isLinked ? (
                    <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-tertiary)" }}>Linked</span>
                  ) : selected === mat._id ? (
                    <CheckCircle2 size={20} className="mrm-item-check" />
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="mrm-footer">
        <AppButton variant="outline" onClick={onClose} disabled={linking}>
          Cancel
        </AppButton>
        <AppButton onClick={handleLink} disabled={!selected || linking}>
          {linking ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Linking…
            </>
          ) : (
            <>
              <CheckCircle2 size={16} className="me-2" />
              Link Material
            </>
          )}
        </AppButton>
      </Modal.Footer>
    </Modal>
  );
};

export default MapMaterialModal;
